<template>
  <div class="h-100 mr-xl-2 ml-xl-2">
    <div class="d-flex flex-column bg-white z-1 h-100">
      <div class="row m-0 p-4">
        <div class="col-8 col-md-4 d-flex align-items-end">
          <h6 class="text-dark mt-2 mt-md-0 mb-0 mb-md-3"><translate>User Name</translate></h6>
        </div>
        <div class="col-8 col-md-4 d-flex align-items-end">
          <h6 class="text-dark mb-3"><translate>Roles</translate></h6>
        </div>
        <div v-if="!disablePermissionChange" class="col-4 d-flex justify-content-end pr-0">
          <LoadingButton class="btn btn-outline-primary btn-raised d-none d-xl-block" :action="saveUserRoles"><i class="fa fa-save"></i>&emsp;<translate>Save</translate></LoadingButton>
          <LoadingButton class="btn btn-outline-primary btn-raised d-xl-none icon-only" :action="saveUserRoles"><i class="fa fa-save"></i></LoadingButton>
        </div>
        <div v-else class="col-md-12">
          <p class="text-dark"><translate>Changing user roles is disabled because roles are managed by the external authentication source.</translate></p>
        </div>
      </div>
      <hr class="m-0">
      <InfiniteList :getAllItems="users" :pageSize="pageSize" v-slot="{ item }">
        <UserListItem :roleOptions="roleOptions" :user="item" :disabled="disablePermissionChange"></UserListItem>
        <hr class="m-0">
      </InfiniteList>
    </div>
  </div>
</template>

<script lang="ts">
import {Options, Vue} from 'vue-class-component'
import AnimatedInput from "@/components/common/AnimatedInput.vue"
import LoadingButton from "@/components/common/LoadingButton.vue"
import InfiniteList from "@/components/common/InfiniteList.vue";
import UserListItem from "@/components/UserListItem.vue";
import {userServiceApi} from "@/api/UserServiceApi";
import User from "@/model/User";
import {settingsServiceApi} from "@/api/SettingsServiceApi";
import SWR from "@/api/SWR";
import Setting from "@/model/Setting";
import {Language, useGettext} from "@jshmrtn/vue3-gettext";
import {useToast} from "vue-toastification";
import {rpcClient} from "@/api/Vue3WebsocketClient";
import SortAndFilterUtil from "@/util/SortAndFilterUtil";

@Options({
  components: {
    InfiniteList, AnimatedInput, LoadingButton, UserListItem
  }
})
export default class Users extends Vue {

  i18n: Language = useGettext()
  toast = useToast()

  pageSize: number = 100
  swr: SWR<Setting[], string[]> | null = null
  roleOptions: { value: string, label: string }[] = [
    { value: 'USER', label: this.i18n.$gettext('User') },
    { value: 'AUDIT', label: this.i18n.$gettext('Auditor') },
    { value: 'ADMIN', label: this.i18n.$gettext('Administrator') }
  ]

  get users(): User[] {
    return userServiceApi.getUsers([ 'userName' ], false).data || []
  }

  get disablePermissionChange(): boolean {
    let setting: Setting | undefined = settingsServiceApi.settings.find(setting => setting.key == 'DISABLE_PERMISSION_MANAGEMENT')
    return Boolean(setting && setting.value === 'true');
  }

  async saveUserRoles(): Promise<void> {
    const admin: User | undefined = this.users.find(u => u.roles && u.roles.indexOf('ADMIN') >= 0)
    if (admin) {
      for (let user of this.users) {
        const thisUser = user
        if (thisUser.userName !== null && thisUser.roles !== null) {
          await userServiceApi._changeUserRoles(thisUser.userName, thisUser.roles).catch(e => {
            this.toast.error(this.i18n.$gettext('Failed to save users.'))
          })
        }
      }
      if (rpcClient.session.user && rpcClient.session.user.userName) {
        const changedUser: User | undefined = userServiceApi.getUser(rpcClient.session.user.userName)
        if (changedUser && !SortAndFilterUtil.arrayEquals(changedUser.roles, rpcClient.session.user.roles)) {
          rpcClient.logout()
        }
      }
    } else {
      this.toast.error(this.i18n.$gettext('You need at least one admin.'))
    }
  }
}
</script>

<style lang="scss">
.is-disabled .multiselect-input {
  background: initial !important;
}
.is-disabled .multiselect-tag {
  background: #47BBC7 !important;
}
</style>
